/**
 * TechnicianAssignment.jsx — Sección compacta para asignar sucursal + bodega
 * a un técnico dentro del formulario de edición de usuario (AdminUsuarios).
 */
import { useState, useEffect } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Building2, Warehouse, Save, Loader2, Trash2 } from "lucide-react";

export default function TechnicianAssignment({ userId, userName, role }) {
  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [branchCode, setBranchCode] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [assigned, setAssigned] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const showFor = ["technician", "supervisor", "soporte_tecnico"].includes(role);

  const loadCurrent = async () => {
    try {
      const { data } = await api.get(`/users/${userId}/assignment`);
      if (data.assigned) {
        setAssigned(data);
        setBranchCode(data.branch_code);
        setWarehouseId(String(data.warehouse_location_id));
      }
    } catch (_) { /* silent */ }
  };
  const loadBranches = async () => {
    try { const { data } = await api.get("/inventory/branches"); setBranches(data); }
    catch (_) { /* silent */ }
  };
  const loadWarehouses = async (b) => {
    if (!b) { setWarehouses([]); return; }
    try {
      const { data } = await api.get("/inventory/warehouses", { params: { branch: b } });
      setWarehouses(data.items || []);
    } catch (_) { /* silent */ }
  };

  useEffect(() => {
    if (!showFor || !userId) return;
    setLoading(true);
    Promise.all([loadCurrent(), loadBranches()]).finally(() => setLoading(false));
    /* eslint-disable-next-line */
  }, [userId]);
  useEffect(() => { loadWarehouses(branchCode); }, [branchCode]);

  const save = async () => {
    if (!branchCode || !warehouseId) { toast.error("Selecciona sucursal y bodega"); return; }
    const wh = warehouses.find(w => String(w.location_id) === String(warehouseId));
    setSaving(true);
    try {
      await api.put(`/users/${userId}/assignment`, {
        branch_code: branchCode,
        warehouse_location_id: Number(warehouseId),
        warehouse_name: wh?.location_name,
        warehouse_complete_name: wh?.location_complete_name,
      });
      toast.success("Asignación guardada");
      loadCurrent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm("¿Quitar la asignación operativa?")) return;
    try {
      await api.delete(`/users/${userId}/assignment`);
      setAssigned(null); setBranchCode(""); setWarehouseId("");
      toast.success("Asignación removida");
    } catch (e) { toast.error("Error al quitar"); }
  };

  if (!showFor) return null;

  return (
    <div className="border-t pt-4 mt-4" data-testid="tech-assignment">
      <div className="flex items-center gap-2 mb-3">
        <Building2 size={16} className="text-slate-500"/>
        <h4 className="font-bold text-sm" style={{ color: "#0A3747" }}>Asignación operativa</h4>
        <span className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Sucursal · Bodega</span>
      </div>
      {loading ? <div className="py-4"><Loader2 className="animate-spin text-gray-400"/></div>
      : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Sucursal</label>
            <select value={branchCode} onChange={(e) => { setBranchCode(e.target.value); setWarehouseId(""); }}
                    className="w-full px-3 py-2 border rounded text-sm"
                    data-testid="assign-branch">
              <option value="">— Seleccionar —</option>
              {branches.map(b => (
                <option key={b.branch} value={b.branch}>{b.branch} · {b.products} prods</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Bodega</label>
            <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}
                    disabled={!branchCode || warehouses.length === 0}
                    className="w-full px-3 py-2 border rounded text-sm disabled:bg-gray-100"
                    data-testid="assign-warehouse">
              <option value="">— Seleccionar —</option>
              {warehouses.map(w => (
                <option key={w.location_id} value={w.location_id}>
                  {w.kind === "central" ? "★ " : "  "}{w.location_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button onClick={save} disabled={saving || !branchCode || !warehouseId}
                className="neu-btn neu-btn-primary text-xs" data-testid="assign-save">
          {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Guardar asignación
        </button>
        {assigned && (
          <>
            <button onClick={clear} className="text-red-600 text-xs flex items-center gap-1" data-testid="assign-clear">
              <Trash2 size={12}/> Quitar
            </button>
            <span className="ml-auto text-[10px] text-gray-500">
              Actualizado {new Date(assigned.updated_at).toLocaleString("es-EC")} por {assigned.updated_by || "?"}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
